import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import './CaseStudies.css';

const caseStudies = [
  {
    id: 1,
    title: 'SchoolAppz',
    challenge: 'Creating a unified platform for schools to manage communication between parents, students, and administrators.',
    solution: 'Developed a comprehensive mobile app with real-time notifications, grade tracking, attendance management, and event scheduling.',
    results: 'Successfully deployed on both Android and iOS platforms, serving thousands of users with seamless cross-platform experience.',
    techStack: ['React Native', 'Firebase', 'Node.js', 'MongoDB'],
    links: {
      playStore: 'https://play.google.com/store/apps/details?id=com.genericsoftware.schoolappz&hl=en_IN&gl=US',
      appStore: 'https://apps.apple.com/in/app/schoolappz/id1523221924'
    },
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  },
  {
    id: 2,
    title: 'CoFynd',
    challenge: 'Building a platform to connect businesses with flexible co-working spaces across multiple cities.',
    solution: 'Created a web application with advanced search filters, booking system, virtual tours, and integrated payment gateway.',
    results: 'Launched successfully with listings in major cities, enabling seamless workspace discovery and booking.',
    techStack: ['React', 'Node.js', 'PostgreSQL', 'Stripe API'],
    links: {
      website: 'https://cofynd.com/'
    },
    gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
  },
  {
    id: 3,
    title: 'Uphance (Stitchex)',
    challenge: 'Developing a mobile sales platform for field sales teams to manage operations and track performance.',
    solution: 'Built native mobile apps with offline capabilities, real-time sync, analytics dashboard, and CRM integration.',
    results: 'Deployed on both platforms, improving sales team productivity by 40% and enabling real-time sales tracking.',
    techStack: ['React Native', 'Redux', 'Firebase', 'REST API'],
    links: {
      playStore: 'https://play.google.com/store/apps/details?id=com.stitchex&hl=en_IN&gl=US',
      appStore: 'https://apps.apple.com/us/app/stitchex-mobile-sales/id1439886336'
    },
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  },
  {
    id: 4,
    title: 'Fitelo / WellFit',
    challenge: 'Creating a comprehensive health and fitness platform with personalized wellness programs.',
    solution: 'Developed a web platform with AI-powered recommendations, progress tracking, meal planning, and workout scheduling.',
    results: 'Launched with thousands of active users, achieving high engagement rates and positive health outcomes.',
    techStack: ['React', 'Python', 'Django', 'PostgreSQL', 'Machine Learning'],
    links: {
      website: 'https://fitelo.co/'
    },
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
];

const CaseStudyCard = ({ study, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const handleLinkClick = (e, url) => {
    e.stopPropagation();
    if (url && url !== '#') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <motion.div
      ref={ref}
      className="case-study-card"
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8, delay: index * 0.2 }}
    >
      <div className="case-study-header" style={{ background: study.gradient }}>
        <h3 className="case-study-title">{study.title}</h3>
      </div>
      
      <div className="case-study-content">
        <div className="case-study-section">
          <h4 className="section-label">Challenge</h4>
          <p className="section-text">{study.challenge}</p>
        </div>
        
        <div className="case-study-section">
          <h4 className="section-label">Solution</h4>
          <p className="section-text">{study.solution}</p>
        </div>
        
        <div className="case-study-section">
          <h4 className="section-label">Results</h4>
          <p className="section-text">{study.results}</p>
        </div>
        
        <div className="case-study-section">
          <h4 className="section-label">Tech Stack</h4>
          <div className="tech-stack">
            {study.techStack.map((tech, idx) => (
              <span key={idx} className="tech-tag">{tech}</span>
            ))}
          </div>
        </div>
        
        <div className="case-study-links">
          {study.links.playStore && (
            <motion.button
              className="case-link-btn"
              onClick={(e) => handleLinkClick(e, study.links.playStore)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              📱 Play Store
            </motion.button>
          )}
          {study.links.appStore && (
            <motion.button
              className="case-link-btn"
              onClick={(e) => handleLinkClick(e, study.links.appStore)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              🍎 App Store
            </motion.button>
          )}
          {study.links.website && (
            <motion.button
              className="case-link-btn"
              onClick={(e) => handleLinkClick(e, study.links.website)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              🔗 View Live Site
            </motion.button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const CaseStudies = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="case-studies" className="case-studies" ref={ref}>
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">Featured Case Studies</h2>
          <p className="section-subtitle">
            Deep dives into our successful projects and the impact they've created
          </p>
        </motion.div>
        
        <div className="case-studies-grid">
          {caseStudies.map((study, index) => (
            <CaseStudyCard key={study.id} study={study} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;

